import { useAssistantState } from '@assistant-ui/react'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { ThreadVariantContext } from '#app/components/assistant-ui/thread.js'
import { type EngineSource } from '#app/routes/resources+/chat.js'
import { visibleAnswerText } from '#app/utils/answer-restart.js'
import { citationNumbering } from '#app/utils/citations.js'
import { extractLastDataPart } from '#app/utils/data-parts.js'
import { trackClickOut } from '#app/utils/plausible.client.js'
import {
	isInternetmedicin,
	sourceDisplayName,
	sourceLongName,
} from '#app/utils/source-classification.js'

/** One entry of the `sources` frame — the server's type, so the two can't drift. */
export type Source = EngineSource

/**
 * Extract sources from message metadata. Returns the LAST sources data part
 * (the engine sends one, before the text).
 */
export function extractSourcesFromMessage(
	metadata: Record<string, unknown> | undefined,
): Source[] | null {
	return extractLastDataPart<Source[]>(metadata, 'sources')
}

/** En post i källistan: källan och numret den VISAS med. */
export type SourceListItem = { source: Source; n: number }

/**
 * Originalnummer -> visat nummer för meddelandet (IM-52), eller null när
 * numren visas som de är. Delas av bubblorna, källistan och kopiera/exportera
 * så att alla tre alltid säger samma siffra för samma källa.
 */
const CitationNumberingContext = createContext<Map<number, number> | null>(null)

/** Null = visa originalnumret (utanför ett meddelande, eller utan källor). */
export function useCitationNumbering() {
	return useContext(CitationNumberingContext)
}

/**
 * Räknar numreringen EN gång per meddelande (se citationNumbering). Motorn
 * skickar hela källramen en gång före texten, så numren är stabila under
 * strömningen. Texten är bara den efter en ev. "börja om"-markör (IM-29): det
 * gamla halva svarets [n] ska inte ta nummer.
 */
export function CitationNumberingProvider({
	children,
}: {
	children: ReactNode
}) {
	// Primitiver ur selektorerna så att providern bara räknar om när de ändras.
	const sourceCount = useAssistantState(
		({ message }) =>
			extractSourcesFromMessage(
				message.metadata as Record<string, unknown> | undefined,
			)?.length ?? 0,
	)
	const text = useAssistantState(({ message }) =>
		visibleAnswerText(
			message.content
				.flatMap(part => (part.type === 'text' ? [part.text] : []))
				.join('\n'),
		),
	)
	const numbering = useMemo(
		() => (sourceCount > 0 ? citationNumbering(text, sourceCount) : null),
		[text, sourceCount],
	)
	return (
		<CitationNumberingContext.Provider value={numbering}>
			{children}
		</CitationNumberingContext.Provider>
	)
}

/**
 * Which of the injected sources the footer lists, and with which number.
 *
 * Motorn skickar HELA listan modellen fick (upp till 13 poster) före texten,
 * och svarets `[n]` indexerar den. Listan visar därför bara de källor svaret
 * faktiskt citerar, numrerade 1, 2, 3 som bubblorna och i den ordningen
 * (IM-52). Medan svaret strömmar utan citat än visas inget; ett färdigt svar
 * utan citat (blankt, avbrutet, blockerat) visar hela listan i ramens ordning,
 * eftersom källorna fortfarande är svarets underlag. Utan numrering (null,
 * bara utanför ett meddelande) visas listan som den är.
 */
export function selectVisibleSources(
	sources: Source[],
	opts: { numbering: Map<number, number> | null; running: boolean },
): SourceListItem[] {
	const all = sources.map((source, index) => ({ source, n: index + 1 }))
	const { numbering } = opts
	if (!numbering) return all
	if (numbering.size > 0) {
		return [...numbering].map(([original, n]) => ({
			source: sources[original - 1] as Source,
			n,
		}))
	}
	return opts.running ? [] : all
}

export function MessageSources() {
	const sources = useAssistantState(({ message }) =>
		extractSourcesFromMessage(
			message.metadata as Record<string, unknown> | undefined,
		),
	)
	const running = useAssistantState(
		({ message }) =>
			message.role === 'assistant' && message.status.type === 'running',
	)
	const numbering = useCitationNumbering()

	if (!sources || sources.length === 0) {
		return null
	}

	return (
		<SourcesFooter
			items={selectVisibleSources(sources, { numbering, running })}
		/>
	)
}

/** The "Källor" footer itself, pure props so it can be rendered in tests. */
export function SourcesFooter({ items }: { items: SourceListItem[] }) {
	if (items.length === 0) {
		return null
	}

	// En platt lista i nummerordning med källnamnet på varje rad (IM-52).
	// Grupperna Internetmedicin/externa hade blandat ihop numren så fort
	// svaret citerar en extern källa först ("IM: 2, 3 · Externa: 1").
	return (
		<div className="mt-4 border-t pt-3" data-sources-footer>
			<p className="mb-2 text-xs font-medium text-muted-foreground">Källor</p>
			<IndexedSourceList items={items} />
		</div>
	)
}

function IndexedSourceList({ items }: { items: SourceListItem[] }) {
	return (
		<ol className="space-y-1">
			{items.map(({ source, n }) => (
				// Keyed on the number too: several retrieved chunks can share one
				// URL (a long PDF split into sections has no per-chunk fragment),
				// and the URL alone would then be a duplicate key.
				<SourceItem key={`${n}-${source.url}`} source={source} number={n} />
			))}
		</ol>
	)
}

function SourceItem({ source, number }: { source: Source; number: number }) {
	const isIM = isInternetmedicin(source.url)
	const variant = useContext(ThreadVariantContext)
	const location =
		variant === 'embed' ? 'assistent_embed' : 'assistent_start_page'
	// Kort namn till klickspårningen (partnerrapporternas register), utskrivet
	// namn i listan: här finns plats och inget expanderat kort att luta sig mot.
	const sourceName = sourceDisplayName(source)
	const longName = sourceLongName(source)
	const title = source.title || sourceName
	// Källnamnet före titeln så användaren ser vem som säger vad, också
	// Internetmedicin i den platta listan (IM-52). En post utan korpuskod eller
	// titel visar bara det namn den har (en rad, som förut).
	const showName = !!source.source && !!source.title
	const label = showName ? `${longName}: ${title}` : title
	return (
		// Flerradiga poster (utskrivet namn + titel) linjerar numret mot första
		// raden.
		<li
			className={`flex ${showName ? 'items-baseline' : 'items-center'} gap-1.5 text-xs`}
		>
			<span
				className={`shrink-0 text-[11px] font-medium ${isIM ? 'text-green-700' : 'text-orange-700'}`}
			>
				{number}.
			</span>
			<a
				href={source.url}
				target="_blank"
				rel="noopener noreferrer"
				onClick={() =>
					trackClickOut({
						destinationUrl: source.url,
						destinationSource: sourceName,
						position: 'sources_footer',
						isInternal: isIM,
						location,
					})
				}
				// Utskrivna namn + titel får radbrytas (även långa ord) i stället för att
				// klippas; enradiga poster behåller truncate. Hela texten som tooltip.
				className={`${showName ? 'min-w-0 break-words ' : 'truncate '}text-foreground/80 hover:text-foreground hover:underline`}
				title={showName ? label : undefined}
				aria-label={`${label} (öppnas i ny flik)`}
			>
				{showName && (
					<>
						<span
							className={`font-medium ${isIM ? 'text-green-700' : 'text-orange-700'}`}
						>
							{longName}
						</span>
						<span aria-hidden="true"> · </span>
					</>
				)}
				{title}
			</a>
		</li>
	)
}
